import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
const base_url = `${process.env.REACT_APP_BASE_URL}`;

// Fetch all IP addresses
export const fetchAllIPAddresses = createAsyncThunk(
  "ipAddress/fetchAll",
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.userToken;
      const response = await axios.get(`${base_url}/api/ip-addresses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Fetch active IP addresses (public endpoint, no auth required)
export const fetchActiveIPAddresses = createAsyncThunk(
  "ipAddress/fetchActive",
  async (_, thunkAPI) => {
    try {
      const response = await axios.get(`${base_url}/api/active-ip-addresses`);
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Create a new IP address
export const createIPAddress = createAsyncThunk(
  "ipAddress/create",
  async (ipData, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.userToken;
      const response = await axios.post(`${base_url}/api/ip-addresses`, ipData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Update an existing IP address
export const updateIPAddress = createAsyncThunk(
  "ipAddress/update",
  async ({ id, ipData }, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.userToken;
      const response = await axios.put(`${base_url}/api/ip-addresses/${id}`, ipData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Delete an IP address
export const deleteIPAddress = createAsyncThunk(
  "ipAddress/delete",
  async (id, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const token = state.auth.userToken;
      await axios.delete(`${base_url}/api/ip-addresses/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return id; // Return the ID for removal from state
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  ipAddresses: [],
  activeIPAddresses: [],
  loading: false,
  error: null,
  success: false,
  ipAddressesTotal: 0,
};

const ipAddressSlice = createSlice({
  name: "ipAddress",
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.error = null;
    },
    resetSuccess: (state) => {
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    // Fetch all IP addresses
    builder
      .addCase(fetchAllIPAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllIPAddresses.fulfilled, (state, action) => {
        state.loading = false;
        state.ipAddresses = action.payload;
        state.ipAddressesTotal = action.payload.length;
      })
      .addCase(fetchAllIPAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch active IP addresses
    builder
      .addCase(fetchActiveIPAddresses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveIPAddresses.fulfilled, (state, action) => {
        state.loading = false;
        state.activeIPAddresses = action.payload;
      })
      .addCase(fetchActiveIPAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Create a new IP address
    builder
      .addCase(createIPAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createIPAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.ipAddresses.push(action.payload);
        state.ipAddressesTotal += 1;
      })
      .addCase(createIPAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });

    // Update an IP address
    builder
      .addCase(updateIPAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateIPAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.ipAddresses = state.ipAddresses.map((ip) =>
          ip._id === action.payload._id ? action.payload : ip
        );
      })
      .addCase(updateIPAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });

    // Delete an IP address
    builder
      .addCase(deleteIPAddress.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteIPAddress.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.ipAddresses = state.ipAddresses.filter((ip) => ip._id !== action.payload);
        state.ipAddressesTotal -= 1;
      })
      .addCase(deleteIPAddress.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      });
  },
});

export const { clearErrors, resetSuccess } = ipAddressSlice.actions;
export default ipAddressSlice.reducer;
